import React, { useEffect } from 'react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { SEOHero, SEOSection, FeatureGrid, BenefitList, InternalLinkGrid, CTABanner } from '../../components/SEOPageComponents';
import { Shield, Zap, FileText, Code, BarChart3, Clock, RefreshCw } from 'lucide-react';

export default function MyInvoisApi() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="MyInvois API Integration — Automate LHDN e-Invoice Submission"
        description="StayMii integrates with LHDN's MyInvois API to automate e-invoice submission for hotels, PMS, and channel managers. Real-time validation, error handling, and compliance tracking."
        keywords="myinvois api, myinvois api integration, lhdn myinvois api, myinvois e invoice, myinvois api hotel, myinvois submission, einvois api malaysia"
        canonicalUrl="/myinvois-api"
      />
      <div className="min-h-screen bg-white">
        <LandingNavBar />
        <SEOHero
          badge="MyInvois API"
          h1="MyInvois API Integration: Automated e-Invoice Submission to LHDN"
          subtitle="StayMii connects directly to LHDN's MyInvois API to submit, validate, and track e-invoices in real-time. Built for hotels, PMS systems, channel managers, and business applications."
        />

        <SEOSection h2="How StayMii Integrates with MyInvois API" description="The MyInvois API is LHDN's official interface for e-invoice submission. StayMii handles the entire API integration so you don't have to.">
          <FeatureGrid items={[
            { icon: Code, title: 'Direct API Connection', desc: 'StayMii connects directly to the MyInvois API. No middleware, no delays. Every invoice is submitted in real-time.' },
            { icon: Shield, title: 'Pre-Submission Validation', desc: 'Each e-invoice is validated against LHDN specifications before submission to prevent rejections and compliance gaps.' },
            { icon: RefreshCw, title: 'Automatic Retry & Error Handling', desc: 'Failed submissions are automatically retried with intelligent error handling. No invoices slip through the cracks.' },
            { icon: BarChart3, title: 'Real-Time Status Tracking', desc: 'Monitor every invoice submission in real-time. See accepted, pending, and rejected invoices at a glance.' },
            { icon: Zap, title: 'Batch & Individual Submission', desc: 'Submit invoices individually or in batches. StayMii optimises the approach based on volume and timing.' },
            { icon: Clock, title: 'Zero Technical Setup', desc: 'No API development needed on your end. StayMii handles authentication, endpoints, and data formatting.' },
          ]} />
        </SEOSection>

        <SEOSection h2="MyInvois API Workflow" bg="bg-slate-50">
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Data Capture', desc: 'StayMii pulls booking and payment data from your PMS or channel manager automatically.' },
              { step: '02', title: 'Invoice Generation', desc: 'Data is formatted into LHDN-compliant e-invoice structure with all required fields.' },
              { step: '03', title: 'Validation', desc: 'Each invoice is validated locally against MyInvois specifications before submission.' },
              { step: '04', title: 'API Submission', desc: 'Validated invoices are submitted to LHDN via MyInvois API with real-time status tracking.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 text-center">
                <span className="text-2xl font-bold text-[#2563EB]">{item.step}</span>
                <h3 className="text-base font-bold text-slate-900 mt-3 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </SEOSection>

        <SEOSection h2="Related Solutions">
          <InternalLinkGrid links={[
            { to: '/e-invoice-malaysia', title: 'e-Invoice Malaysia', desc: 'Complete e-invoicing guide' },
            { to: '/lhdn-e-invoice', title: 'LHDN e-Invoice', desc: 'LHDN compliance requirements' },
            { to: '/hotel-e-invoice-system', title: 'Hotel e-Invoice', desc: 'Purpose-built for hotel operations' },
            { to: '/pms-e-invoice-integration', title: 'PMS Integration', desc: 'Connect your PMS' },
            { to: '/channel-manager-e-invoice', title: 'Channel Manager', desc: 'OTA booking automation' },
            { to: '/airbnb-e-invoice-malaysia', title: 'Airbnb e-Invoice', desc: 'Short-term rental compliance' },
          ]} />
        </SEOSection>

        <CTABanner title="Let StayMii Handle Your MyInvois API Integration" subtitle="No API development needed. Go live with automated e-invoice submission in days." />
        <LandingFooter />
      </div>
    </>
  );
}
